import type { Item } from '@/types/prioritization/lifecycle';
import { formatItemFieldValue } from '@/lib/print/itemPrintPreferences';

export interface ItemOnePagerBodyProps {
  item: Item;
  queueLabel?: string | null;
}

function fmtShortDate(d: Date | undefined | null): string {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function MetaChip({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value?.trim()) return null;
  return (
    <span className="inline-flex items-baseline gap-1 rounded border border-[var(--rule)] bg-[var(--surface)] px-1.5 py-0.5 text-[10.5px] print:border-[var(--rule)]">
      <span className="font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-ter)]">{label}</span>
      <span className="text-[var(--text-pri)] print:text-black">{value}</span>
    </span>
  );
}

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value?.trim()) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[9.5px] font-semibold uppercase tracking-widest text-[var(--text-ter)] print:text-[var(--text-ter)]">
        {label}
      </dt>
      <dd className="text-[11.5px] text-[var(--text-pri)] print:text-black leading-snug">{value}</dd>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 text-[9px] font-bold uppercase tracking-[0.12em] text-[#5A7D5E] print:text-[#354738] border-b border-[#D9E8DC] print:border-[#B5D1BA] pb-1">
      {children}
    </h2>
  );
}

export function ItemOnePagerBody({ item, queueLabel }: ItemOnePagerBodyProps) {
  const fmtOpts = { queueLabel };

  const description = formatItemFieldValue('description', item, fmtOpts);
  const desiredOutcome = formatItemFieldValue('desired_outcome', item, fmtOpts);
  const beneficiary = formatItemFieldValue('beneficiary', item, fmtOpts);
  const workType = formatItemFieldValue('work_type', item, fmtOpts);
  const strategicBucket = formatItemFieldValue('strategic_bucket', item, fmtOpts);
  const altitudeLevel = formatItemFieldValue('altitude_level', item, fmtOpts);
  const itemCategory = formatItemFieldValue('item_category', item, fmtOpts);
  const metaCategory = formatItemFieldValue('meta_category', item, fmtOpts);
  const exitStatus = formatItemFieldValue('exit_status', item, fmtOpts);
  const planningMaturity = formatItemFieldValue('planning_maturity', item, fmtOpts);
  const knowledgeStatus = formatItemFieldValue('knowledge_status', item, fmtOpts);
  const confidenceLevel = formatItemFieldValue('confidence_level', item, fmtOpts);
  const source = formatItemFieldValue('source', item, fmtOpts);
  const queueValue = formatItemFieldValue('queue_hint', item, fmtOpts);
  const tags = (item.tags ?? []).filter(Boolean);

  const altitudeDisplay = altitudeLevel ? `L${altitudeLevel}` : null;

  const hasStatusSection = !!(planningMaturity || knowledgeStatus || confidenceLevel || exitStatus);
  const hasDetailsSection = !!(queueValue || workType || source);
  const hasRightColumn = hasDetailsSection || hasStatusSection;

  return (
    <div className="text-[12px] leading-relaxed text-[var(--text-pri)] print:text-black">

      {/* ── Header ── */}
      <div className="print-field-block mb-5 flex items-start gap-5">
        {/* Green accent bar */}
        <div className="w-1 self-stretch rounded-full bg-[#5A7D5E] print:bg-[#465F49] shrink-0" />

        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#5A7D5E] print:text-[#465F49]">
            Priorities.ai · Item Brief
          </p>
          <h1 className="text-[21px] font-bold leading-[1.15] tracking-tight text-[var(--text-pri)] print:text-black">
            {item.title?.trim() || 'Untitled Item'}
          </h1>
          {/* Meta chips row */}
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <MetaChip label="Category" value={itemCategory} />
            <MetaChip label="Meta" value={metaCategory} />
            <MetaChip label="Altitude" value={altitudeDisplay} />
            <MetaChip label="Bucket" value={strategicBucket} />
          </div>
        </div>

        {/* Top-right stamp */}
        <div className="shrink-0 text-right space-y-1 pt-0.5">
          <p className="text-[10px] tabular-nums text-[var(--text-ter)] print:text-[var(--text-ter)]">
            {fmtShortDate(new Date())}
          </p>
          <p className="font-mono text-[10px] text-[var(--text-ter)] print:text-[var(--text-ter)]">
            {item.id.slice(0, 8)}&hellip;
          </p>
        </div>
      </div>

      {/* ── Body: two columns ── */}
      <div
        className="grid gap-0 border-t border-[var(--rule)] print:border-[var(--rule)] pt-4"
        style={{ gridTemplateColumns: hasRightColumn ? '1fr 13rem' : '1fr' }}
      >
        {/* Left: narrative content */}
        <div className={`space-y-5 ${hasRightColumn ? 'pr-5' : ''}`}>
          {description ? (
            <section className="print-field-block space-y-1.5">
              <SectionLabel>Description</SectionLabel>
              <p className="whitespace-pre-wrap text-[12px] text-[var(--text-pri)] print:text-black leading-relaxed">
                {description}
              </p>
            </section>
          ) : null}

          {desiredOutcome ? (
            <section className="print-field-block space-y-1.5">
              <SectionLabel>Desired Outcome</SectionLabel>
              <p className="whitespace-pre-wrap text-[12px] text-[var(--text-pri)] print:text-black leading-relaxed">
                {desiredOutcome}
              </p>
            </section>
          ) : null}

          {beneficiary ? (
            <section className="print-field-block space-y-1.5">
              <SectionLabel>Beneficiary</SectionLabel>
              <p className="text-[12px] text-[var(--text-pri)] print:text-black">{beneficiary}</p>
            </section>
          ) : null}
        </div>

        {/* Right: metadata column */}
        {hasRightColumn ? (
          <div className="border-l border-[var(--rule)] print:border-[var(--rule)] pl-5 space-y-5">
            {hasDetailsSection ? (
              <section className="print-field-block">
                <SectionLabel>Details</SectionLabel>
                <dl className="space-y-2.5">
                  <FieldRow label="Work type" value={workType} />
                  <FieldRow label="Queue" value={queueValue} />
                  <FieldRow label="Source" value={source} />
                  <FieldRow label="Created" value={fmtShortDate(item.createdAt)} />
                  <FieldRow label="Updated" value={fmtShortDate(item.updatedAt)} />
                </dl>
              </section>
            ) : null}

            {hasStatusSection ? (
              <section className="print-field-block">
                <SectionLabel>Status</SectionLabel>
                <dl className="space-y-2.5">
                  <FieldRow label="Lifecycle" value={exitStatus} />
                  <FieldRow label="Planning maturity" value={planningMaturity} />
                  <FieldRow label="Knowledge" value={knowledgeStatus} />
                  <FieldRow label="Confidence" value={confidenceLevel} />
                </dl>
              </section>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ── Footer: tags ── */}
      {tags.length > 0 ? (
        <div className="print-field-block mt-5 border-t border-[var(--rule)] print:border-[var(--rule)] pt-3 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-[var(--rule)] print:border-[var(--rule)] bg-[var(--surface)] print:bg-[var(--bg-raised)] px-2 py-0.5 text-[10.5px] text-[var(--text-sec)] print:text-[var(--text-sec)]"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      {/* ── Watermark footer line ── */}
      <div className="mt-5 border-t border-[var(--rule)] print:border-[var(--rule)] pt-2 flex justify-between text-[9px] text-[var(--text-ter)] print:text-[var(--text-ter)]">
        <span>Priorities.ai</span>
        <span className="font-mono">{item.id}</span>
      </div>
    </div>
  );
}
